import "./common.css";
import { ICustomTypography, ImageBadgeNamePropsType } from "./types";
import { getFirstTwoInitialsOfName } from "./utils";
import { ROOT_REDUCER_ACTIONS, store } from "../store/store";
import { ReactNode, useState } from "react";
import { CircularProgress, TypographyStyle } from "@mui/material";
import { Typography as AntdTypography, Avatar } from "antd";
import Typography from "../components/Typography/Typography";
import Tooltip from "../components/Tooltip/Tooltip";
import { IPropsToCustomOrEmptyComp } from "../components/AntdTable/AntdTable.types";

import emptyBox from "../assets/images/ecommerce.svg";
import { UploadActionBtn } from "../components/Buttons/Buttons";
import auditService from "../services/audit.service"; // ✨ NEW
import type { LogoutType } from './types';

export function ImageBadgeName({
  name,
  url,
  height = "32px",
  width = "32px",
  firstTwoInitials = false,
  style,
}: ImageBadgeNamePropsType) {
  const nameInitials = getFirstTwoInitialsOfName(name, firstTwoInitials);
  return (
    <Avatar
      shape="circle"
      style={{
        height: height,
        width: width,
        background: "#2e96ff",
        ...style,
      }}
      src={url}
    >
      {nameInitials}
    </Avatar>
  );
}

export const GetLink = ({
  text,
  style,
  href,
}: {
  text: string | undefined;
  style?: TypographyStyle;
  href?: string;
}) => {
  const [tooltipText, setTooltipText] = useState<string>("Copy");
  const handleOnClick = async () => {
    try {
      await navigator.clipboard.writeText(text ?? "");
      setTooltipText("Copied!");
    } catch (err) {
      setTooltipText("Copy failed");
    }
    setTimeout(() => {
      setTooltipText("Copy");
    }, 2500);
  };

  return (
    <Tooltip
      title={<Typography fontSize={12}>{tooltipText}</Typography>}
      placement="bottom"
    >
      <AntdTypography.Link href={href} style={style} onClick={handleOnClick}>
        {text}
      </AntdTypography.Link>
    </Tooltip>
  );
};

// Guards against logout() running more than once per tab lifetime. Several
// call sites can legitimately fire it back-to-back for the same event — e.g.
// the token-refresh 401 deadlock fix triggers it from both the inner
// (client2) and outer (client) interceptor catch blocks — and without this,
// each duplicate call posts its own /audit_log/logout/ row, polluting the
// usage-analytics logout metrics with phantom extra logouts for one real
// event. JS is single-threaded, so this synchronous check-and-set at the top
// (before any `await`) is sufficient — no real concurrency to race against.
// Deliberately never reset: logout() always ends in a hard redirect
// (window.location.replace), which tears down this module entirely.
let logoutInProgress = false;

export async function logout(logoutType: LogoutType = 'MANUAL') {
  if (logoutInProgress) {
    return;
  }
  logoutInProgress = true;

  // Track logout event. sessionStorage ('userSession') is the primary source;
  // fall back to localStorage ('lastSessionId') for cases where sessionStorage
  // is absent (new tab, page refresh, or forced logout before AuthContainer mount).
  const sessionData = auditService.getSessionData();
  const fallbackSessionId = localStorage.getItem('lastSessionId');

  if (sessionData || fallbackSessionId) {
    try {
      const sessionId = sessionData?.sessionId || fallbackSessionId || undefined;
      await auditService.trackLogout(sessionData?.userId ?? '', sessionId, logoutType);
    } catch (error) {
      console.error('Failed to track logout, continuing with logout process:', error);
    }
  }

  // Clean up unconditionally — these markers must not survive into the next
  // session even if session data was absent or audit tracking was skipped.
  localStorage.removeItem('lastSessionId');
  localStorage.removeItem('loginTimestamp');

  store.dispatch({ type: ROOT_REDUCER_ACTIONS.LOGOUT });
}

export const RenderComponent = (Comp: any, data: any, props: {}): ReactNode => {
  return <Comp {...props} />;
};

export const CustomTypography = ({ style, children }: ICustomTypography) => {
  return (
    <span style={{ ...style, fontFamily: "Google Sans" }}>{children}</span>
  );
};

export const BetaTag = ({ isGradient, isMini }: any) => {
  return (
    <div
      className={`betaTag ${isGradient ? "betaTagGradient" : "betaTagDefault"}
      ${isMini ? "betaTagMiniSize" : "betaTagDefaultSize"}  
      `}
    >
      <Typography
        fontSize={isMini ? 10 : 14}
        fontWeight={400}
        color={isGradient ? "white" : "#282B31"}
      >
        Beta
      </Typography>
    </div>
  );
};

export const LiveTag = ({ isMini }: any) => {
  return (
    <div
      className={`liveTag ${isMini ? "liveTagMiniSize" : "liveTagDefaultSize"}`}
    >
      <div className={` ${isMini ? "liveTagDotMiniSize" : "liveTagDot"}`}></div>
      <Typography fontSize={isMini ? 10 : 14} fontWeight={400} color="#1E9B52">
        Live
      </Typography>
    </div>
  );
};

export const getPaginationComp = (Comp: any) => {
  return <div className="pagination">{Comp}</div>;
};

export const NoDataCompOnEmptyTable: React.FC<IPropsToCustomOrEmptyComp> = ({
  pagination,
  uploadActionBtnText,
  onUploadActionClick,
  noDataText = "No data available",
  withBorder = false,
  hideActionButton = false,
}) => {
  return (
    <div className={`noDataComp ${withBorder ? "noDataCompWithBorder" : ""}`}>
      <div className="noDataContent">
        <img src={emptyBox} alt="No data available" />
        <Typography fontSize={12} fontWeight={400} color="#7A7F89">
          {noDataText}
        </Typography>
        {!hideActionButton && (
          <UploadActionBtn
            text={uploadActionBtnText}
            onClick={onUploadActionClick}
            style={{ marginTop: "1rem" }}
          />
        )}
      </div>
    </div>
  );
};

export const ComponentWithRotatingBorder: React.FC<any> = ({
  children,
  className,
}) => {
  return (
    <div className={`card card_target ${className}`}>
      <div className="inner">{children}</div>
    </div>
  );
};

export const DocumentProgressBar: React.FC<{
  text?: string;
  processedCount: number;
  totalCount: number;
}> = ({ text = "Documents Reviewed", processedCount, totalCount }) => {
  return (
    <div className="loadingTag">
      <CircularProgress size={12} color="inherit" />
      <Typography fontWeight={400} fontSize={10} ellipsis={true}>
        {text} {"("} {processedCount} {"/"} {totalCount} {")"}
      </Typography>
    </div>
  );
};
